import lsqMarvinExternalAppIntegrator from '@lsq-marvin-app-utility/external-app-integrator';
import Axios, { AxiosInstance, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import MXCache from 'Common/CacheManager/MXCache';
import { AppMode } from 'Common/Enums/metadata.enum';
import {
    AuthResponse,
    UserAuthResponse,
} from 'Common/Models/authresponse.model';
import MXLogger from 'Common/MXLogger';
import { getMobileOSType, getOSType } from 'Common/Utils/CheckDevice';
import { BannerState } from 'Components/Reusable/Banner/banner.component';
import { clear, createStore, setMany, UseStore } from 'idb-keyval';
import {
    getMobileAuthCookie,
    getMobileAuthCookieFromCb,
} from 'utils/getMobileCookies';
import {
    handleMobileExceptions,
    handleMobileToSetUserAuthToken,
} from 'utils/mobileCallbackHandle';
import APM, { LogActionType } from 'Common/APM';
import { FailureType } from 'Common/APM/apm.enum';
import MXEventEmitter from './MXEventEmitter';

interface JWTPayload {
    exp?: number;
    [key: string]: unknown;
}

const checkAuthorizedfailed = (error: any, mode: AppMode) =>
    error?.config?.url === 'authorize' ||
    (error?.config?.url === 'isauthorized' &&
        [AppMode.MarvinFullScreen, AppMode.FullScreen].includes(mode));

const checkIsAPMLogApi = (error: any) =>
    error?.config?.url === 'instrument/apm' ||
    error?.config?.url === '/instrument/apm';

export enum RequestTypeEnum {
    Contacts = 1,
    ConnectorConfig = 2,
    MessageHistory = 3,
    LeadDetails = 4,
    SessionTimeout = 5,
}

const getBannerMessage = (requestType?: RequestTypeEnum | undefined) => {
    switch (requestType) {
        case RequestTypeEnum.Contacts:
            return 'We are unable to load the chats right now. Please refresh the page to retry.';
        case RequestTypeEnum.ConnectorConfig:
            return 'Something went wrong. Please refresh the page to retry.';
        case RequestTypeEnum.LeadDetails:
            return 'We are unable to load the chat history at the moment. Please refresh the page to retry.';
        case RequestTypeEnum.MessageHistory:
            return 'We are unable to load the chat right now. Please refresh the page to retry.';
        case RequestTypeEnum.SessionTimeout:
            return 'Session timeout';
        default:
            return 'Something went wrong, Please refresh the page or Contact Us if the problem persists.';
    }
};

const appModeDecider = () => {
    const mode = MXCache.GetCacheItem(MXCache.AppModeKey);
    // if fullscreen, check if open in iframe, bcoz fullscreen and MarvinFullscreen has same domain
    if ([2, 3].includes(mode)) {
        if (window.parent === window.self) {
            return 2;
        }
        return 3;
    }
    return mode;
};

enum SocketInfraVersionEnum {
    v1 = 1,
    v2 = 2,
}

export const setUserAuthResponseToMXCache = (
    userAuthResponse: UserAuthResponse
) => {
    MXCache.SetCacheItem(
        MXCache.LeadMergeFeildsKey,
        userAuthResponse.leadMergeFields,
        480
    );
    MXCache.SetCacheItem(
        MXCache.IsBotEnabled,
        userAuthResponse.isBOTEnabled,
        480
    );
    MXCache.SetCacheItem(
        MXCache.UserDetailKey,
        userAuthResponse.userFields,
        480
    );
    MXCache.SetCacheItem(
        MXCache.LeadPhoneFeildsKey,
        userAuthResponse.leadPhoneFields,
        480
    );
    MXCache.SetCacheItem(
        MXCache.LicensedFeaturesKey,
        userAuthResponse.licensedFeatures,
        480
    );
    MXCache.SetCacheItem(
        MXCache.HistorySizeKey,
        userAuthResponse.historySize,
        480
    );

    MXCache.SetCacheItem(
        MXCache.SystemUserKey,
        userAuthResponse.systemUser,
        480
    );

    MXCache.SetCacheItem(
        MXCache.IsServiceCloudEnabled,
        userAuthResponse.isServiceCloudEnabled ?? false,
        600
    );

    MXCache.SetCacheItem(
        MXCache.SocketInfraVersionKey,
        userAuthResponse.socketInfraVersion || SocketInfraVersionEnum.v1,
        480
    );
    MXCache.SetCacheItem(
        MXCache.AvailabilitySettings,
        userAuthResponse.availabilitySettings,
        600
    );
    MXCache.SetCacheItem(
        MXCache.UserPreference,
        userAuthResponse.userPreference.recentContactsFilter,
        600
    );
    MXCache.SetCacheItem(
        MXCache.TokenExpiryTime,
        userAuthResponse.expiresOn,
        600
    );

    MXCache.SetCacheItem(
        MXCache.EnableNotifications,
        userAuthResponse.userPreference.enableNotifications,
        600
    );

    MXCache.SetCacheItem(MXCache.UserRole, userAuthResponse.role, 600);

    MXCache.SetCacheItem(
        MXCache.TemplateMessagesFilter,
        userAuthResponse.userPreference.templateMessagesFilter,
        600
    );
};

export default class API {
    instance: AxiosInstance;

    static redirectUrl = process.env.REACT_APP_AUTH_REDIRECT_URL || '';

    static marvinRedirectUrl =
        process.env.REACT_APP_AUTH_REDIRECT_URL_MARVIN || '';

    static swliteRedirectUrl =
        process.env.REACT_APP_AUTH_REDIRECT_URL_SWLITE || '';

    static disableRedirect = process.env.REACT_APP_DISABLE_REDIRECT || '';

    static osType = getOSType() || 'unknown';

    static appModeStatic = appModeDecider();

    appMode: number;

    tokenGenerating: boolean = false;

    Converse_KeyVal_Store = createStore(
        'Converse_Log',
        'Converse_KeyVal_Store'
    );

    constructor(
        baseURL: string,
        isGuardedRequest = true,
        shouldInterceptResponse = true,
        requestType?: RequestTypeEnum | undefined
    ) {
        this.appMode = API.appModeStatic ? API.appModeStatic : appModeDecider();

        this.instance = Axios.create({
            baseURL,
        });
        const $this = this;
        axiosRetry(this.instance, {
            retries: Number(process.env.REACT_APP_MAX_API_RETRIES) || 5,
            retryDelay: () =>
                Number(process.env.REACT_APP_API_RETRY_COOLDOWN_TIME) || 5000,
            retryCondition: (error) => {
                if (
                    error?.response?.status === 401 &&
                    !checkIsAPMLogApi(error) &&
                    !(error.config as any)?.['__401Retried']
                ) {
                    return true;
                }
                return axiosRetry.isNetworkOrIdempotentRequestError(error);
            },
            onRetry(retryCount, error) {
                if (
                    error?.response?.status === 401 &&
                    !checkIsAPMLogApi(error)
                ) {
                    MXLogger.debug(
                        `401 error detected, clearing token before retry - URL: ${error?.config?.url}, Retry count: ${retryCount}`
                    );
                    API.RemoveAuthToken();
                    (error.config as any)['__401Retried'] = true;
                }
                if (
                    retryCount ===
                        (Number(process.env.REACT_APP_MAX_API_RETRIES) || 5) &&
                    error
                ) {
                    MXEventEmitter.emit('APPLICATION_LEVEL_BANNER', {
                        Message: getBannerMessage(requestType),
                        endpoint: error?.config?.url?.split('?')[0],
                        BannerState: BannerState.Failure,
                        Timeout: 18000000,
                        ID: 'CONNECTION_BANNER',
                    });
                }
            },
        });
        this.AddRequestInterceptor(this.instance, true);

        if (shouldInterceptResponse)
            this.AddResponseInterceptors(this.instance);
    }

    protected Responsify<T>(Response: AxiosResponse) {
        if (Response && Response.status === 200 && Response.data) {
            return Response.data as T;
        }
        if (
            Response &&
            Response.status >= 400 &&
            Response.status !== 413 &&
            Response.status !== 403
        ) {
            // TODO: ReEvaluate logging out on status code greater than 400, 413 added for quick fix for Content Too large, 403 added for quick fix for Forbidden
            MXLogger.debug(
                `LOGOUT TRIGGER: API Response error - Status: ${Response.status}, URL: ${Response.config?.url}, Data: ${JSON.stringify(Response.data)}`
            );
            MXLogger.info('Logout from responsify');
            console.log('Logout from responsify');
            this.Logout();
        }
        return {} as T;
    }

    async postNRLog(logObject: any) {
        const inst = Axios.create({
            baseURL: process.env.REACT_APP_INSIGHTS_BASE_URL || '',
        });
        const authKey = JSON.parse(
            MXCache.GetCacheItem(MXCache.AuthKey) ?? null
        );
        const token = authKey?.Token;
        if (token) {
            return this.Responsify<any>(
                await inst.post('instrument/apm', logObject, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Token: token,
                    },
                })
            );
        }
        console.log('token is empty');
    }

    public async waitUntilTokenIsGenerated(): Promise<string | null> {
        let authToken = API.GetAuthToken();
        if (authToken?.Token && this.isValidJwtToken(authToken?.Token)) {
            return authToken?.Token;
        }
        if (this.tokenGenerating) {
            return new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (!this.tokenGenerating) {
                        resolve(API.GetAuthToken()?.Token || null);
                        clearInterval(interval);
                    }
                }, 500);
            });
        }

        this.tokenGenerating = true;
        const regionID = process.env.REACT_APP_REGION_ID || '1';
        const isAuthorized = await this.authorize(
            this.appMode,
            authToken?.Token,
            regionID
        );
        if (!isAuthorized) {
            this.tokenGenerating = false;
            return null;
        }
        const newAuthToken = API.GetAuthToken();
        this.tokenGenerating = false;
        return newAuthToken?.Token || null;
    }

    private AddRequestInterceptor = (
        instance: AxiosInstance,
        isGuardedRequest = false
    ) => {
        const $this = this;
        instance.interceptors.request.use(
            async (requestConfig) => {
                if (requestConfig?.headers) {
                    if (isGuardedRequest) {
                        requestConfig.headers.APP_MODE = `${this.appMode}`;
                        requestConfig.headers.OS_TYPE = `${API.osType}`;

                        const authToken =
                            await $this.waitUntilTokenIsGenerated();
                        if (authToken) {
                            requestConfig.headers.Authorization = `Bearer ${authToken}`;
                            requestConfig.headers.token = authToken;
                        } else {
                            throw new Error('Token not generated');
                        }
                    }
                }
                return requestConfig;
            },
            (error) => {
                MXLogger.info(
                    'Logout from request interceptor, error: ' + error
                );
                $this.Logout(null, () => {
                    throw new Error('Logout failed');
                });
                return Promise.reject(error);
            }
        );
    };

    private AddResponseInterceptors = (instance: AxiosInstance) => {
        const $this = this;
        instance.interceptors.response.use(
            (response) => {
                MXEventEmitter.emit('remove', {
                    endpoint: response?.config?.url?.split('?')[0] ?? '',
                    BannerState: BannerState.Failure,
                    Timeout: 18000000,
                    ID: 'CONNECTION_BANNER',
                });
                // Adding end time for API response time tracking for new relic
                const requestStartTime =
                    response?.config?.['axios-retry']?.lastRequestTime;
                const responseEndTime = new Date().getTime();
                const responseTime =
                    responseEndTime - (requestStartTime || responseEndTime);
                APM.logAction(LogActionType.API_CALL, {
                    payload: response?.config?.data,
                    url: response?.config?.url,
                    startTime: requestStartTime ? requestStartTime : 'NA',
                    amznRequestId:
                        response?.headers?.['x-amzn-requestid'] || 'NA', // Need to expose this from server to access here
                    endTime: responseEndTime,
                    timeDiff: responseTime === 0 ? 'NA' : responseTime,
                    status: response?.status,
                    method: response?.config?.method,
                });
                return response;
            },
            (error) => {
                MXLogger.info(error);
                // don't logout for new relic api failure as it just a log api and
                // failure of this API should not affect the functionality of the application
                // Aborted/cancelled requests (and network failures) have no
                // `response`; guard so the interceptor doesn't throw and mask
                // the real error of the caller (e.g. the initialLoad fetch).
                const { config, ...restOfError } = error.response ?? {};
                // Adding end time for API response time tracking for new relic
                const requestStartTime =
                    config?.['axios-retry']?.lastRequestTime;
                const responseEndTime = new Date().getTime();
                const responseTime =
                    responseEndTime - (requestStartTime || responseEndTime);
                APM.logAction(LogActionType.API_CALL, {
                    payload: error?.response?.config?.data,
                    requestAuthToken:
                        error?.response?.status === 401
                            ? error?.response?.config?.headers?.Authorization
                            : 'NA',
                    url: error?.response?.config?.url,
                    amznRequestId:
                        error?.response?.headers?.['x-amzn-requestid'] || 'NA', // Need to expose this from server to access here
                    startTime: requestStartTime ? requestStartTime : 'NA',
                    endTime: responseEndTime,
                    timeDiff: responseTime === 0 ? 'NA' : responseTime,
                    status: error?.response?.status,
                    method: error?.response?.config?.method,
                    data: error?.response?.data,
                    errorObject: restOfError,
                });
                if (
                    error &&
                    error.response &&
                    error.response.status === 401 &&
                    !checkIsAPMLogApi(error)
                ) {
                    MXLogger.debug(
                        `LOGOUT TRIGGER: 401 Unauthorized error - URL: ${error?.config?.url}, AppMode: ${this.appMode}, Data: ${JSON.stringify(error?.response?.data)}`
                    );

                    if ((error.config as any)?.['__401Retried']) {
                        const { appMode } = this;
                        if (appMode === AppMode.Widget) {
                            const user = MXCache.GetCacheItem(
                                MXCache.UserDetailKey
                            );

                            this.postNRLog({
                                appMode,
                                appType: '',
                                orgId: user?.orgCode ?? '',
                                userId: user?.userId ?? '',
                                eventType: 'error',
                                widgetBreak: false,
                                error: JSON.stringify(error),
                                endpoint: error?.config?.url ?? '',
                                status: error.response?.status ?? '',
                                failedApi: '',
                                failedStatusCode: '',
                            });
                        } else {
                            APM.noticeError(error, {
                                failureType: FailureType.API_ERROR,
                                endpoint: error?.config?.url ?? '',
                                status: error.response?.status ?? '',
                            });
                        }
                        if (
                            this.appMode === AppMode.LsqMobile ||
                            checkAuthorizedfailed(error, this.appMode)
                        ) {
                            MXLogger.debug(
                                `LOGOUT TRIGGER: Logging out due to 401 error - AppMode: ${this.appMode}, IsMobile: ${this.appMode === AppMode.LsqMobile}, CheckAuthorizedFailed: ${checkAuthorizedfailed(error, this.appMode)}`
                            );
                            $this.Logout();
                        } else if (
                            [
                                AppMode.Widget,
                                AppMode.MarvinMobile,
                                AppMode.MarvinWidget,
                            ].includes(this.appMode)
                        ) {
                            const isAppAlreadyRefreshed = MXCache.GetCacheItem(
                                MXCache.IsAppRefreshed
                            );
                            // just to avoid infinite app reload I am adding a cache of 3 minutes to recall the app reload if any 401 error throws in api
                            if (isAppAlreadyRefreshed) {
                                $this.Logout();
                                // emit event to show ErrorComponent
                            } else {
                                MXCache.Clear();
                                MXCache.SetCacheItem(
                                    MXCache.IsAppRefreshed,
                                    true,
                                    3
                                );
                                window.location.reload();
                            }
                        } else {
                            MXEventEmitter.emit('APPLICATION_LEVEL_BANNER', {
                                Message: getBannerMessage(
                                    RequestTypeEnum.SessionTimeout
                                ),
                                BannerState: BannerState.Failure,
                                Timeout: 18000000,
                                ID: 'SESSION_TIMEOUT_BANNER',
                            });
                        }
                    }
                } else if (!error.response) {
                    // this else if condition only to capture the error response which is undefined
                    if (this.appMode === AppMode.Widget) {
                        const user = MXCache.GetCacheItem(
                            MXCache.UserDetailKey
                        );
                        // don't log for new relic api failure
                        if (!checkIsAPMLogApi(error)) {
                            this.postNRLog({
                                appMode: this.appMode,
                                appType: '',
                                orgId: user?.orgCode ?? '',
                                userId: user?.userId ?? '',
                                eventType: 'error',
                                widgetBreak: false,
                                endpoint: error?.config?.url ?? '',
                                error: JSON.stringify(error),
                                status: error.response?.status ?? '',
                                failedApi: '',
                                failedStatusCode: '',
                            });
                        }
                    } else {
                        APM.noticeError(error, {
                            failureType: FailureType.NETWORK_ERROR,
                            endpoint: error?.config?.url ?? '',
                            status: error.response?.status ?? '',
                        });
                    }
                }

                return Promise.reject(error);
            }
        );
    };

    protected static GetAuthToken(): AuthResponse | null {
        const authToken = MXCache.GetCacheItem(MXCache.AuthKey) || '';
        if (authToken && authToken.length > 0) {
            return JSON.parse(authToken) as AuthResponse;
        }
        return null;
    }

    protected static RemoveAuthToken() {
        MXCache.RemoveOneItem(MXCache.AuthKey);
    }

    protected static SetAuthToken(authResponse: AuthResponse) {
        MXCache.SetCacheItem(
            MXCache.AuthKey,
            JSON.stringify(authResponse),
            480
        );
        MXCache.SetCacheItem(
            MXCache.CopyAuthKey,
            JSON.stringify(authResponse),
            480
        );
        (window as any).MXNotificationClient.updateAuthToken(
            JSON.stringify(authResponse)
        );
    }

    LogoutApi = async (authToken: AuthResponse | null) => {
        if (authToken) {
            const inst = Axios.create({
                baseURL: process.env.REACT_APP_API_SERVICE_BASE_URL || '',
            });
            await inst.get('auth/user/logout', {
                headers: {
                    Authorization: `Bearer ${authToken.Token}`,
                    token: authToken.Token,
                },
            });
        }
    };

    Logout(redirectUrl: string | null = null, cb: Function | null = null) {
        console.log('Logout from platform called');
        if ((window as any).MXNotificationClient?.closeSharedWorker) {
            console.log('Closing shared worker');
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            (window as any).MXNotificationClient?.closeSharedWorker();
        }
        let redirectTo =
            this.appMode === AppMode.MarvinFullScreen
                ? API.marvinRedirectUrl
                : API.redirectUrl;
        if (redirectUrl && redirectUrl.length) {
            redirectTo = redirectUrl;
        }
        const authToken = API.GetAuthToken();
        MXCache.Clear();
        if (this.appMode === AppMode.LsqMobile) {
            // this method is to reset the user authorization information stored in mobile device cache, this cache we are storing at the first logic,
            // and using the same cache when we trying to open after that, so when we faced 401 in iur API, we are here just
            // clearing it from mobile cache.
            handleMobileToSetUserAuthToken({}, true);
        }
        clear(this.Converse_KeyVal_Store);
        this.LogoutApi(authToken)
            .then(() => {
                if (API.disableRedirect !== '1') {
                    if (this.appMode === AppMode.MarvinFullScreen) {
                        try {
                            lsqMarvinExternalAppIntegrator.context.get({
                                callBackFn: (data: any) => {
                                    const redirectMarvinOrSWLite =
                                        data?.isSWLite
                                            ? API.swliteRedirectUrl
                                            : API.marvinRedirectUrl;
                                    window.open(redirectMarvinOrSWLite, '_top');
                                },
                            });
                        } catch (err) {
                            console.log('App integrator object not available');
                        }
                    } else window.open(redirectTo, '_self');
                }
            })
            .catch((e) => {
                MXLogger.error(new Error(e));
            })
            .finally(() => {
                MXCache.Clear();
                if (cb && typeof cb === 'function') {
                    try {
                        cb(true);
                    } catch (error) {
                        MXLogger.error(new Error(String(error)));
                    }
                }
            });
    }

    isValidJwtToken(token: string | null | undefined): boolean {
        if (!token) return false;
        token = token.trim();
        if (token === '') return false;

        // Check basic JWT format (three parts separated by dots)
        const parts = token.split('.');
        if (parts.length !== 3) return false;

        // Check if parts are non-empty and base64 formatted
        if (!parts.every((part) => part && /^[A-Za-z0-9_-]+$/g.test(part)))
            return false;

        try {
            const [, payload] = parts;
            // Add padding if needed
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64.padEnd(
                base64.length + ((4 - (base64.length % 4)) % 4),
                '='
            );

            const decoded = JSON.parse(atob(padded)) as JWTPayload;

            // Validate decoded payload
            if (decoded === null || typeof decoded !== 'object') return false;

            if (typeof decoded.exp !== 'number') return true;
            return Date.now() < decoded.exp * 1000;
        } catch {
            return false; // treat as expired if invalid
        }
    }

    public async authorize(
        mode: number,
        authorization?: string | null,
        regionID: string = '1',
        converseStore?: UseStore,
        loopCheckToken?: () => void
    ): Promise<boolean> {
        try {
            MXLogger.debug(
                `authorize() called - Mode: ${mode}, Authorization: ${authorization ? 'Present' : 'Missing'}`
            );
            let staticAuth = '';
            let dynamicAuth = '';

            if (mode === AppMode.LsqMobile) {
                const mobileAuth = getMobileAuthCookie();
                const { STATICAUTH = '', DYNAMICAUTH = '' } = mobileAuth;
                dynamicAuth = DYNAMICAUTH;
                staticAuth = STATICAUTH;
                if (
                    !(DYNAMICAUTH || STATICAUTH) &&
                    getMobileOSType() === 'ios'
                ) {
                    const {
                        STATICAUTH: mobileStaticAuth = '',
                        DYNAMICAUTH: mobileDynamicAuth = '',
                    } = (await getMobileAuthCookieFromCb()) as any;

                    dynamicAuth = mobileDynamicAuth;
                    staticAuth = mobileStaticAuth;
                }
            } else {
                if (
                    [
                        AppMode.MarvinFullScreen,
                        AppMode.MarvinWidget,
                        AppMode.MarvinMobile,
                    ].includes(mode)
                ) {
                    MXLogger.debug(
                        `Validating Marvin token for Marvin app mode: ${mode}. Token: ${authorization ? 'Present' : 'Missing'}`
                    );
                    if (!this.isValidJwtToken(authorization)) {
                        MXLogger.debug(
                            'LOGOUT TRIGGER: Marvin token is invalid or expired, logging out user. Token: ' +
                                authorization
                        );
                        const reissuedToken =
                            await this.getMarvinReissueToken();
                        if (!reissuedToken) {
                            MXLogger.debug(
                                'LOGOUT TRIGGER: Marvin token reissue failed, logging out user. Token: ' +
                                    reissuedToken
                            );
                            this.Logout();
                            return false;
                        }
                        authorization = reissuedToken;
                    }
                    MXLogger.debug('Marvin token validation passed');
                } else {
                    MXLogger.debug(
                        `Non-Marvin app mode (${mode}) - skipping Marvin token validation, proceeding to authorize API call`
                    );
                }
            }

            if (mode === AppMode.LsqMobile && !(dynamicAuth || staticAuth)) {
                return false;
            }

            if (
                document.referrer != null &&
                (document.referrer.toLowerCase().indexOf('/home/signin') !=
                    -1 ||
                    document.referrer.toLowerCase().indexOf('https://login') !=
                        -1)
            ) {
                MXCache.Clear();
            }
            MXCache.SetCacheItem(MXCache.AppModeKey, mode);

            const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL || '';
            const authInstance = Axios.create({
                baseURL: authBaseUrl,
            });

            const userAuthResponse = this.Responsify<UserAuthResponse>(
                await authInstance.get('authorize', {
                    withCredentials: true,
                    headers: {
                        LSQ_RID: regionID,
                        APP_MODE: `${mode}`,
                        Authorization: authorization as any,
                        ...(mode === AppMode.LsqMobile && {
                            ...(staticAuth && { STATIC_AUTH: staticAuth }),
                            ...(dynamicAuth && {
                                DYNAMIC_AUTH: dynamicAuth,
                            }),
                        }),
                    },
                })
            );

            if (
                userAuthResponse &&
                userAuthResponse.token &&
                userAuthResponse.token.length
            ) {
                if (mode === AppMode.LsqMobile) {
                    handleMobileToSetUserAuthToken(userAuthResponse);
                }
                API.SetAuthToken({
                    Token: userAuthResponse.token,
                    ExpiresOn: userAuthResponse.expiresOn,
                } as AuthResponse);

                if (converseStore) {
                    setMany(
                        [
                            ['Token', userAuthResponse.token],
                            ['ExpiresOn', userAuthResponse.expiresOn],
                        ],
                        converseStore
                    );
                }
                setUserAuthResponseToMXCache(userAuthResponse);
                if (loopCheckToken) {
                    loopCheckToken();
                }
                return true;
            }

            this.Logout();

            return false;
        } catch (e) {
            MXLogger.error(e);

            if (mode === AppMode.LsqMobile) {
                handleMobileExceptions(
                    (e as any).response?.data?.exceptionDetails
                );
            }
            return false;
        }
    }

    public async getMarvinReissueToken(): Promise<string | null> {
        if (window.parent === window.self) {
            return null;
        }
        return new Promise((resolve) => {
            const waitTimeout = setTimeout(() => {
                resolve(null);
            }, 60 * 1000);
            lsqMarvinExternalAppIntegrator.actionDispatcher.reIssueTokens({
                callBackFn: (data: any) => {
                    if (data?.Tokens?.Token) {
                        clearTimeout(waitTimeout);
                        resolve(data?.Tokens);
                    } else {
                        resolve(null);
                    }
                },
            });
        });
    }
}
